import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import {
  Activity,
  ArrowUpRight,
  BadgeCheck,
  CheckCircle2,
  Gauge,
  Network,
  TrendingUp,
} from "lucide-react";
import BridgeWidget from "./BESCBridgeWidget";
import { BRIDGE_TOKENS, CHAIN_LABELS, getExplorerLink } from "./bridgeConfig";

const revealTransition = { duration: 0.55, ease: [0.22, 1, 0.36, 1] };

const staggerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const riseVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: revealTransition,
  },
};

const TYPICAL_SETTLEMENT_TIME = "2-5 min";

const formatCompactValue = (value, { prefix = "", suffix = "" } = {}) => {
  const safeValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  return `${prefix}${new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: safeValue >= 100 ? 0 : 1,
  }).format(safeValue)}${suffix}`;
};

const formatPercent = (value) => `${Number.isFinite(Number(value)) ? Number(value).toFixed(Number(value) >= 99 ? 2 : 1) : "0.0"}%`;

const formatTimestamp = (value) => {
  if (!value) {
    return "Updating shortly";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Updating shortly";
  }

  return date.toLocaleString();
};

const formatRelativeLabel = (value, fallbackMinutes = 1) => {
  if (!value) {
    return `${fallbackMinutes} min ago`;
  }

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return `${fallbackMinutes} min ago`;
  }

  const diffMinutes = Math.max(1, Math.round((Date.now() - timestamp) / 60000));
  if (diffMinutes < 60) {
    return `${diffMinutes} min ago`;
  }

  const diffHours = Math.round(diffMinutes / 60);
  return `${diffHours} hr ago`;
};

const shortenHash = (value) => {
  if (!value || value.length < 14) {
    return value || "";
  }
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
};

const getTokenKeyFromType = (type = "") => {
  const normalizedType = type.toLowerCase();
  if (normalizedType.includes("wsol")) {
    return "WSOL";
  }
  if (normalizedType.includes("wxrp")) {
    return "WXRP";
  }
  if (normalizedType.includes("money")) {
    return "MONEY";
  }
  if (normalizedType.includes("wbnb")) {
    return "WBNB";
  }
  if (normalizedType.includes("weth")) {
    return "WETH";
  }
  if (normalizedType.includes("wusdc")) {
    return "WUSDC";
  }
  return "WBESC";
};

const formatWholeNumberString = (value) => value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const formatTokenAmount = (amount, tokenKey, sourceChain) => {
  // WUSDC: DB stores amount in BESC destination units (6 dec) — always use BESC decimals.
  // All other tokens: DB stores amount in source chain units — use source decimals.
  const token = BRIDGE_TOKENS[tokenKey];
  const decimals = tokenKey === 'WUSDC'
    ? (token?.chains?.BESC?.decimals ?? 6)
    : (token?.chains?.[sourceChain]?.decimals ?? 18);
  const value = String(amount || "0");

  try {
    const negative = value.startsWith("-");
    const raw = negative ? value.slice(1) : value;
    const padded = raw.padStart(decimals + 1, "0");
    const whole = padded.slice(0, -decimals) || "0";
    const fraction = padded.slice(-decimals).replace(/0+$/, "").slice(0, 4);
    const formattedWhole = formatWholeNumberString(whole.replace(/^0+(?=\d)/, ""));
    return `${negative ? "-" : ""}${formattedWhole}${fraction ? `.${fraction}` : ""}`;
  } catch {
    return "0";
  }
};

const fetchStatusSummary = async () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, "");
  if (!backendUrl) {
    return null;
  }

  const response = await fetch(`${backendUrl}/status/summary`, {
    method: "GET",
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`Status summary request failed with ${response.status}`);
  }

  return response.json();
};

const getChainTone = (chain) => {
  if (chain?.outage) {
    return "down";
  }
  if (chain?.degraded) {
    return "degraded";
  }
  return "online";
};

const BridgeHomePage = () => {
  const { data: statusSummary, isLoading } = useQuery({
    queryKey: ["bridge-status-summary"],
    queryFn: fetchStatusSummary,
    staleTime: 20000,
    refetchInterval: 30000,
    retry: 1,
  });

  const totals = statusSummary?.bridge?.totals || {};
  const chains = statusSummary?.rpc?.chains || [];
  const routes = statusSummary?.bridge?.routes || [];
  const recentCompleted = statusSummary?.bridge?.recentCompleted || [];
  const successRate = totals.total30d ? Math.max(0, 100 - Number(totals.failureRate30d || 0)) : 100;
  const topRoute = routes[0];
  const liveStatusLabel = isLoading
    ? "Status: Syncing"
    : statusSummary?.status === "degraded"
      ? "Status: Degraded"
      : statusSummary?.status === "outage"
        ? "Status: Offline"
        : "Status: Online";
  const tickerItems = [
    "BESC cross-chain rail",
    "Move assets across chains with confidence.",
    "Secure transfers across supported networks with live status and clear delivery updates.",
    "Fee: 0.10%",
    `Est. time: ${TYPICAL_SETTLEMENT_TIME}`,
    liveStatusLabel,
  ];

  const stats = [
    {
      label: "All-time transfers",
      value: Number(totals.totalAllTime || 0),
      icon: Activity,
    },
    {
      label: "Last 30 days",
      value: Number(totals.completed30d || 0),
      icon: TrendingUp,
    },
    {
      label: "Success rate",
      value: successRate,
      icon: BadgeCheck,
      render: formatPercent(successRate),
    },
    {
      label: "Supported routes",
      value: Number(routes.length || 0),
      icon: Network,
    },
    {
      label: "Busiest route",
      value: 0,
      icon: Gauge,
      detail: topRoute ? `${formatCompactValue(topRoute.total || 0)} recent transfers` : "",
      render: topRoute ? `${topRoute.sourceChain} -> ${topRoute.destinationChain}` : "Updating",
    },
  ];

  const completedBridgeItems = recentCompleted.length
    ? recentCompleted.slice(0, 5).map((bridge, index) => {
        const tokenKey = getTokenKeyFromType(bridge.type);
        const token = BRIDGE_TOKENS[tokenKey];
        const sourceChain = bridge.sourceChain;
        const destinationChain = bridge.destinationChain;
        const viewHash = bridge.destinationTxHash || bridge.txHash;
        const viewChain = bridge.destinationTxHash ? destinationChain : sourceChain;

        return {
          id: `${bridge.txHash}-${index}`,
          amount: formatTokenAmount(bridge.amount, tokenKey, sourceChain),
          tokenLabel: token?.label || tokenKey,
          sourceLabel: CHAIN_LABELS[sourceChain] || sourceChain,
          destinationLabel: CHAIN_LABELS[destinationChain] || destinationChain,
          txHash: shortenHash(viewHash),
          age: formatRelativeLabel(bridge.completedAt, index + 1),
          explorerLink: getExplorerLink(viewChain, viewHash),
        };
      })
    : [
        {
          id: "warmup-1",
          amount: "",
          tokenLabel: "Live feed",
          sourceLabel: "Completed bridges",
          destinationLabel: "Updating automatically",
          txHash: "",
          age: "Checking now",
          explorerLink: "",
        },
      ];
  const networkRows = [
    ...chains.map((chain) => ({
      label: chain.displayName,
      tone: getChainTone(chain),
      meta:
        chain.healthy && chain.endpoints?.length
          ? `${chain.healthyCount}/${chain.totalEndpoints} endpoints healthy`
          : "Checking status",
    })),
    {
      label: "Bridge service",
      tone: statusSummary?.status === "outage" ? "down" : statusSummary?.status === "degraded" ? "degraded" : "online",
      meta: statusSummary?.generatedAt ? `Updated ${formatTimestamp(statusSummary.generatedAt)}` : "Status unavailable right now",
    },
  ];

  return (
    <div className="premium-home">
      <motion.section className="premium-home__hero" initial="hidden" animate="visible" variants={staggerVariants}>
        <motion.div className="premium-home__ticker" variants={riseVariants}>
          <div className="premium-home__ticker-track">
            {[...tickerItems, ...tickerItems].map((item, index) => (
              <span key={`${item}-${index}`}>{item}</span>
            ))}
          </div>
        </motion.div>

        <motion.div className="premium-home__widget-slot" variants={riseVariants}>
          <BridgeWidget summary={statusSummary} summaryLoading={isLoading} />
        </motion.div>
      </motion.section>

      <motion.section
        className="premium-stats-grid"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
        variants={staggerVariants}
      >
        {stats.map((stat) => {
          const Icon = stat.icon;
          const hasCustomRender = Boolean(stat.render);

          return (
            <motion.article key={stat.label} className="premium-stat-card" variants={riseVariants} whileHover={{ y: -4 }}>
              <div className="premium-stat-card__icon">
                <Icon size={18} />
              </div>
              <div className="premium-stat-card__body">
                <span className="premium-stat-card__label">{stat.label}</span>
                <strong className="premium-stat-card__value">
                  {hasCustomRender ? (
                    stat.render
                  ) : (
                    <CountUp end={stat.value} duration={1.2} separator="," preserveValue={false} />
                  )}
                </strong>
                {stat.detail ? <p className="premium-stat-card__detail">{stat.detail}</p> : null}
              </div>
            </motion.article>
          );
        })}

      </motion.section>

      <motion.section
        className="premium-insight-grid"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerVariants}
      >
        <motion.article className="premium-surface" variants={riseVariants}>
          <div className="premium-surface__header">
            <div>
              <p className="premium-surface__eyebrow">Network status</p>
              <h2>Network status</h2>
            </div>
            <span className="premium-surface__stamp">{formatTimestamp(statusSummary?.generatedAt)}</span>
          </div>

          <div className="premium-health-list">
            {networkRows.map((row) => (
              <div key={row.label} className="premium-health-row">
                <div className="premium-health-row__label">
                  <span className={`premium-health-indicator premium-health-indicator--${row.tone}`} />
                  <span>{row.label}</span>
                </div>
                <span className="premium-health-row__meta">{row.meta}</span>
              </div>
            ))}
          </div>
        </motion.article>

        <motion.article className="premium-surface" variants={riseVariants}>
          <div className="premium-surface__header premium-surface__header--activity">
            <div className="premium-activity-title">
              <p className="premium-surface__eyebrow">Live activity</p>
              <span className="premium-live-stamp">
                <span />
                Newest completions
              </span>
            </div>
          </div>

          <div className="premium-activity-feed">
            {completedBridgeItems.length ? (
              completedBridgeItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  className={`premium-bridge-event${index === 0 ? " is-latest" : ""}`}
                  initial={{ opacity: 0, y: 16, scale: 0.985 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.42, delay: index * 0.045, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -3 }}
                >
                  <span className="premium-bridge-event__glow" />
                  <div className="premium-bridge-event__main">
                    <div className="premium-bridge-event__topline">
                      <div className="premium-bridge-event__amount">
                        {item.amount ? <strong>{item.amount}</strong> : null}
                        <span>{item.tokenLabel}</span>
                      </div>
                      <span className="premium-bridge-event__status">
                        <CheckCircle2 size={14} />
                        Delivered
                      </span>
                    </div>

                    <div className="premium-bridge-route">
                      <span className="premium-bridge-route__chain">
                        <small>From</small>
                        {item.sourceLabel}
                      </span>
                      <span className="premium-bridge-route__rail">
                        <span />
                      </span>
                      <span className="premium-bridge-route__chain">
                        <small>To</small>
                        {item.destinationLabel}
                      </span>
                    </div>

                    <div className="premium-bridge-event__foot">
                      <span>{item.age}</span>
                      {item.txHash ? <span>Tx {item.txHash}</span> : null}
                    </div>
                  </div>

                  <div className="premium-bridge-event__actions">
                    {item.explorerLink ? (
                      <a className="premium-activity-link" href={item.explorerLink} target="_blank" rel="noreferrer">
                        View
                        <ArrowUpRight size={13} />
                      </a>
                    ) : (
                      <span className="premium-activity-badge">Live</span>
                    )}
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="premium-empty-state">
                <strong>Recent completed bridges will show here soon.</strong>
                <p>Newly delivered transfers appear automatically.</p>
              </div>
            )}
          </div>
        </motion.article>
      </motion.section>

      <motion.section
        className="premium-support-grid"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerVariants}
      >
        <motion.article className="premium-support-card" variants={riseVariants} whileHover={{ y: -4 }}>
          <p className="premium-support-card__eyebrow">Apply for bridge listing</p>
          <h3>Launch your asset on the BESC bridge</h3>
          <p>
            Submit project details, confirm maintenance billing, and move through review with a cleaner institutional presentation.
          </p>
          <Link className="premium-support-card__cta" to="/apply">
            Apply for Bridge Listing
            <ArrowUpRight size={16} />
          </Link>
        </motion.article>

        <motion.article className="premium-support-card premium-support-card--recovery" variants={riseVariants} whileHover={{ y: -4 }}>
          <p className="premium-support-card__eyebrow">Transfer support</p>
          <h3>Need help with a transfer?</h3>
          <p>Contact our support team with your source transaction hash and we'll investigate and resolve your bridge manually.</p>
          <a className="premium-support-card__cta" href="mailto:support@besc.services?subject=Bridge Transfer Support">
            Contact Support
            <ArrowUpRight size={16} />
          </a>
        </motion.article>
      </motion.section>
    </div>
  );
};

export default BridgeHomePage;
